"use client";

import { cva } from "class-variance-authority";
import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import React, { type ReactElement } from "react";
import { HomeSectionHeader, ThemeTile } from "@/components";
import { ThemeTypeEnum } from "@/types";
import { Button } from "@oreme/ui";
import Link from "next/link";
import Image from "next/image";
import { ArrowRight } from "lucide-react";

const themeTileVariants = cva(
  "transform transition-transform scale-100 hover:drop-shadow-lg hover:-translate-y-2 hover:scale-105",
);

function HomeSectionTheme(): ReactElement {
  const t = useTranslations("home.SectionTheme");

  return (
    <section className="relative -mt-8">
      <div className="relative z-10 max-w-screen-2xl mx-auto flex flex-col py-12">
        <HomeSectionHeader title={t("title")} lead={t("lead")} />
        <ul className="relative px-4 z-20 list-none grid gap-4 grid-col-1 md:grid-cols-2 lg:grid-cols-4">
          {Object.keys(ThemeTypeEnum).map((key, index) => (
            <motion.li
              key={key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.4,
                delay: index * 0.2,
                scale: { type: "spring", visualDuration: 0.4, bounce: 0.5 },
              }}
              whileInView="onscreen"
            >
              <ThemeTile
                theme={ThemeTypeEnum[key as keyof typeof ThemeTypeEnum]}
                className={themeTileVariants()}
              />
            </motion.li>
          ))}
        </ul>
        <footer className="text-center mt-8 px-4">
          <Button className="border-0 flex sm:inline-flex" asChild>
            <Link href="/sites">
              {t("action")} <ArrowRight strokeWidth={1} />
            </Link>
          </Button>
        </footer>
      </div>
      <figure className="absolute inset-0 top-auto">
        <Image
          src="https://static.oreme.org/shared/illustrations/landscape2.svg"
          alt="Landscape"
          width={3840}
          height={512}
          loading="eager"
        />
      </figure>
    </section>
  );
}

export { HomeSectionTheme };
